// core/data/videos.ts — typed seed for the placeholder Videos page.
//
// Creators are NOT new placeholder accounts: they're existing seeded users
// (core/data/users.ts, mirrored server-side in backend/apps/accounts/management/
// commands/seed.py's AUTHORS list) so Follow genuinely works through the real
// useFollow -> followsService -> /api/users/<handle>/follow/ path with zero new
// backend records. See handoff notes for why this was chosen over minting new
// video-specific creator accounts.
//
// `ts` (recency sort key, higher = newer) is derived directly from `ageLabel` —
// keep them in agreement when editing either. Longform ts uses a 100-daysAgo
// scale; shorts use a separate 65-daysAgo scale (the two never sort against each
// other, so the ranges don't need to interleave).
//
// PORTED to mobile/src/core/data/videos.ts (minus the short-form items — mobile
// has no Shorts). Keep both in sync.

import type { VideoItem } from '@/core/models/video';
import { seedCreators } from './creators';

/**
 * Top Creators rail — denormalized display data for real seeded accounts
 * (core/data/users.ts / backend seed.py AUTHORS), so Follow works through the
 * genuine useFollow -> followsService path with zero new backend records.
 * Re-exports the shared `seedCreators` array (core/data/creators.ts) — Live's
 * `seedLiveCreators` re-exports the same array (slice-4-xl S4: these used to
 * be two byte-identical copies).
 */
export const seedVideoCreators = seedCreators;

export const seedVideos: VideoItem[] = [
  {
    id: 'vid-01',
    title: 'Why open dialogue beats censorship every time',
    description:
      'A long-form breakdown of why platforms that default to open speech outperform ones that default to takedowns, with case studies from the last decade of the open web.',
    authorId: 'freedom-seeker',
    category: 'news',
    tags: ['#FreeSpeech', '#OpenWeb'],
    durationSec: 754,
    views: 128_000,
    ageLabel: '3d',
    ts: 97,
    featured: true,
  },
  {
    id: 'vid-02',
    title: 'The case for a decentralized social web',
    description: 'Why identity and data portability matter more than any single feature.',
    authorId: 'liberty-mind',
    category: 'tech',
    tags: ['#Decentralization', '#Privacy'],
    durationSec: 612,
    views: 84_200,
    ageLabel: '5d',
    ts: 95,
    trendingRank: 1,
  },
  {
    id: 'vid-03',
    title: 'Neural nets, explained without the hype',
    description: 'A grounded look at what modern models can and cannot do.',
    authorId: 'open-mind',
    category: 'science',
    tags: ['#AI', '#Explainer'],
    durationSec: 940,
    views: 61_500,
    ageLabel: '1w',
    ts: 93,
    trendingRank: 2,
  },
  {
    id: 'vid-04',
    title: 'A practical privacy toolkit for 2026',
    description: 'Five tools that actually move the needle on day-to-day privacy.',
    authorId: 'maya-patel',
    category: 'tech',
    tags: ['#Privacy', '#Toolkit'],
    durationSec: 503,
    views: 44_900,
    ageLabel: '2d',
    ts: 98,
    trendingRank: 3,
  },
  {
    id: 'vid-05',
    title: 'The maintainers keeping the web free',
    description: 'Profiles of the open-source maintainers whose unglamorous work holds up the free web.',
    authorId: 'jordan-lee',
    category: 'education',
    tags: ['#OpenSource'],
    durationSec: 821,
    views: 39_800,
    ageLabel: '4d',
    ts: 96,
    trendingRank: 4,
  },
  {
    id: 'vid-06',
    title: 'Edge compute and the end of gatekeepers',
    description: 'How pushing compute to the edge quietly breaks a few long-standing chokepoints.',
    authorId: 'sara-kim',
    category: 'tech',
    tags: ['#EdgeCompute'],
    durationSec: 675,
    views: 28_100,
    ageLabel: '6d',
    ts: 94,
    trendingRank: 5,
  },
  {
    id: 'vid-07',
    title: 'Moderation that respects free speech',
    description: 'How a five-category harm model keeps moderation narrow without going hands-off.',
    authorId: 'freedom-seeker',
    category: 'news',
    tags: ['#Moderation', '#FreeSpeech'],
    durationSec: 588,
    views: 51_300,
    ageLabel: '1d',
    ts: 99,
  },
  {
    id: 'vid-08',
    title: 'Build in the open: why it wins',
    description: 'The compounding advantages of shipping in public.',
    authorId: 'liberty-mind',
    category: 'education',
    tags: ['#BuildInPublic'],
    durationSec: 432,
    views: 22_700,
    ageLabel: '1w',
    ts: 93,
  },
  {
    id: 'vid-09',
    title: 'Own your identity on the open web',
    description: 'A tour of self-sovereign identity, minus the jargon.',
    authorId: 'open-mind',
    category: 'tech',
    tags: ['#Identity'],
    durationSec: 719,
    views: 18_400,
    ageLabel: '2w',
    ts: 86,
  },
  {
    id: 'vid-10',
    title: 'Speak freely, listen harder',
    description: 'A conversation about the discipline of good-faith debate online.',
    authorId: 'maya-patel',
    category: 'education',
    tags: ['#Community'],
    durationSec: 560,
    views: 15_900,
    ageLabel: '3d',
    ts: 97,
  },
  {
    id: 'vid-11',
    title: 'Synthwave set: coding at 2am',
    description: 'An hour of ambient synthwave for late builds.',
    authorId: 'jordan-lee',
    category: 'music',
    tags: ['#Synthwave', '#LoFi'],
    durationSec: 3600,
    views: 9_800,
    ageLabel: '2d',
    ts: 98,
  },
  {
    id: 'vid-12',
    title: 'Who owns your game? Mods, servers and the right to tinker',
    description:
      'Community-run servers and the modders keeping games alive long after the studios move on.',
    authorId: 'sara-kim',
    category: 'gaming',
    tags: ['#Gaming', '#Modding'],
    durationSec: 690,
    views: 12_300,
    ageLabel: '5d',
    ts: 95,
  },
  // Shorts — web-only, capped at 60s per the owner decision (the concept's 2:02 is wrong).
  {
    id: 'short-01',
    title: 'One privacy setting everyone skips',
    description: 'Thirty seconds that actually matter.',
    authorId: 'maya-patel',
    category: 'science',
    tags: ['#Privacy'],
    durationSec: 34,
    views: 210_000,
    ageLabel: '1d',
    ts: 64,
    short: true,
  },
  {
    id: 'short-02',
    title: 'Free speech, one breath',
    description: 'The five-category harm model in one breath.',
    authorId: 'freedom-seeker',
    category: 'news',
    tags: ['#FreeSpeech'],
    durationSec: 48,
    views: 176_000,
    ageLabel: '2d',
    ts: 63,
    short: true,
  },
  {
    id: 'short-03',
    title: 'Decentralization in 45 seconds',
    description: 'Why identity portability is the whole game.',
    authorId: 'liberty-mind',
    category: 'tech',
    tags: ['#Decentralization'],
    durationSec: 45,
    views: 98_000,
    ageLabel: '3d',
    ts: 62,
    short: true,
  },
  {
    id: 'short-04',
    title: 'Open source, closed wallet',
    description: 'The funding gap in one clip.',
    authorId: 'jordan-lee',
    category: 'education',
    tags: ['#OpenSource'],
    durationSec: 52,
    views: 64_000,
    ageLabel: '4d',
    ts: 61,
    short: true,
  },
  {
    id: 'short-05',
    title: 'Good-faith debate, fast',
    description: 'A 40-second case for listening harder.',
    authorId: 'open-mind',
    category: 'education',
    tags: ['#Community'],
    durationSec: 40,
    views: 41_000,
    ageLabel: '5d',
    ts: 60,
    short: true,
  },
];
